import React, { useCallback, useRef, useState } from 'react'
import { VStack } from '@musinsa/mwms-ds'
import { FormProvider, useForm } from 'react-hook-form'

import { AgGridReact } from 'ag-grid-react'
import { PrimaryButton } from '@musinsa/layer-components-desktop'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import SingleBatchSettingForm, { INIT_FORM } from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingForm'
import { SingleBatchSettingTable } from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingTable'
import SingleBatchSettingRegisterModal, {
  SingleBatchSettingRegisterModalRef,
} from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingRegisterModal'
import SingleBatchSettingResultDialog, { ResultVariant } from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingResultDialog'
import SingleBatchSettingDeleteConfirmDialog from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingDeleteConfirmDialog'
import SingleBatchSettingPeriodEditModal from '@/pages/outbound/singleBatchSetting/components/SingleBatchSettingPeriodEditModal'
import { GetSingleBatchSettingsReq, SingleBatchSettingItem } from '@/services/outbound/singleBatchSetting/types'
import { useDeleteBundleConditions, useGetSingleBatchSettings, useUpdateBundleConditionStatus } from '@/services/outbound/singleBatchSetting/useSingleBatchSettingQueries'
import { AxiosError } from 'axios'
import MSnackBar from '@/utils/modal/MSnackBar'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'

function SingleBatchSetting() {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<GetSingleBatchSettingsReq>(INIT_FORM)
  const form = useForm<GetSingleBatchSettingsReq>({ defaultValues: { ...INIT_FORM, ...parsedQs } })
  const gridRef = useRef<AgGridReact>(null)
  const registerModalRef = useRef<SingleBatchSettingRegisterModalRef>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [countPerPage, setCountPerPage] = useState(100)
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const [resultVariant, setResultVariant] = useState<ResultVariant | null>(null)
  const [failedRows, setFailedRows] = useState<string | undefined>()
  const [failureMessage, setFailureMessage] = useState<string | undefined>()
  const [deleteConfirmRows, setDeleteConfirmRows] = useState<SingleBatchSettingItem[] | null>(null)
  const [periodEditRow, setPeriodEditRow] = useState<SingleBatchSettingItem | null>(null)

  const [queryParams, setQueryParams] = useState<GetSingleBatchSettingsReq>({
    warehouseCode: parsedQs.warehouseCode,
    barcodes: parsedQs.barcodes,
    page: 0,
    size: 100,
  })

  const { data, isFetching, refetch } = useGetSingleBatchSettings(queryParams)
  const { mutateAsync: deleteBundleConditions, isPending: isDeleting } = useDeleteBundleConditions()
  const { mutateAsync: updateBundleConditionStatus, isPending: isStatusUpdating } = useUpdateBundleConditionStatus()
  const items = data?.items ?? []
  const totalCount = data?.pageInfo?.totalCount ?? 0

  const handleSubmit = useCallback(
    (formData: GetSingleBatchSettingsReq) => {
      gridRef.current?.api?.deselectAll()
      const newParams: GetSingleBatchSettingsReq = {
        warehouseCode: formData.warehouseCode || undefined,
        barcodes: formData.barcodes?.length ? formData.barcodes : undefined,
        page: 0,
        size: queryParams.size,
      }
      MenuStorage.menuOnSession.setSearch(newParams)
      setCurrentPage(0)
      setQueryParams(newParams)
      reload()
      replaceQuery(newParams)
    },
    [queryParams.size, reload, replaceQuery],
  )

  const handleChangePage = useCallback((page: number) => {
    gridRef.current?.api?.deselectAll()
    setCurrentPage(page)
    setQueryParams((prev) => ({ ...prev, page }))
  }, [])

  const handleCountPerPageChange = useCallback((size: number) => {
    setCountPerPage(size)
    setCurrentPage(0)
    setQueryParams((prev) => ({ ...prev, size, page: 0 }))
  }, [])

  const handleReset = useCallback(() => {
    form.reset(INIT_FORM)
    MenuStorage.menuOnSession.setSearch(INIT_FORM)
    setCurrentPage(0)
    setQueryParams({ page: 0, size: countPerPage })
    reload()
    replaceQuery({})
  }, [form, countPerPage, reload, replaceQuery])

  const handleOpenRegisterModal = useCallback(() => setIsRegisterModalOpen(true), [])
  const handleCloseRegisterModal = useCallback(() => setIsRegisterModalOpen(false), [])

  const handleRegisterSuccess = useCallback(() => {
    MSnackBar.show('단수합포 설정이 등록되었습니다', 'success')
    setIsRegisterModalOpen(false)
    refetch()
  }, [refetch])

  const handleRegisterPartialFailure = useCallback((rows: string, message?: string) => {
    setFailedRows(rows)
    setFailureMessage(message)
    setResultVariant('partialFailure')
  }, [])

  const handleExcelFailure = useCallback((message?: string) => {
    setFailureMessage(message)
    setResultVariant('excelFailure')
  }, [])

  const handleDirectFailure = useCallback((message: string) => {
    setIsRegisterModalOpen(false)
    setFailureMessage(message)
    setResultVariant('directFailure')
  }, [])

  const handleCloseResult = useCallback(() => {
    setResultVariant(null)
    setFailedRows(undefined)
    setFailureMessage(undefined)
  }, [])

  const handleReupload = useCallback(() => {
    registerModalRef.current?.triggerFileInput()
  }, [])

  const handleFileReselected = useCallback(() => {
    setResultVariant(null)
    setFailedRows(undefined)
    setFailureMessage(undefined)
  }, [])

  const onDelete = useCallback((rows: SingleBatchSettingItem[]) => setDeleteConfirmRows(rows), [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmRows) return
    try {
      await deleteBundleConditions({ ids: deleteConfirmRows.map((r) => r.id) })
      setDeleteConfirmRows(null)
      refetch()
      MSnackBar.show('단수합포 설정이 삭제되었습니다', 'success')
    } catch (e) {
      const err = e as AxiosError<ApiResponseV2>
      const message = err.response?.data?.meta?.message || '단수합포 설정 삭제에 실패했습니다.'
      MSnackBar.show(message, 'critical')
    }
  }, [deleteConfirmRows, deleteBundleConditions, refetch])

  const handleDeleteConfirmClose = useCallback(() => setDeleteConfirmRows(null), [])

  const handleStatusChange = useCallback(async (row: SingleBatchSettingItem) => {
    const newStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    try {
      await updateBundleConditionStatus({ id: row.id, status: newStatus })
      await refetch()
      MSnackBar.show('적용여부가 변경되었습니다', 'success')
    } catch (e) {
      const err = e as AxiosError<ApiResponseV2>
      const message = err.response?.data?.meta?.message || '적용여부 변경에 실패했습니다.'
      MSnackBar.show(message, 'critical')
    }
  }, [updateBundleConditionStatus, refetch])

  const handlePeriodEdit = useCallback((row: SingleBatchSettingItem) => setPeriodEditRow(row), [])
  const handlePeriodEditClose = useCallback(() => setPeriodEditRow(null), [])
  const handlePeriodEditSuccess = useCallback(() => {
    setPeriodEditRow(null)
    refetch()
  }, [refetch])
  const handlePeriodEditDirectFailure = useCallback((message: string) => {
    setPeriodEditRow(null)
    setFailureMessage(message)
    setResultVariant('directFailure')
  }, [])

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection
            title="단수합포 설정"
            customRight={<PrimaryButton variant="filled" size="large" label="등록" onClick={handleOpenRegisterModal} />}
          />
          <SingleBatchSettingForm isLoading={isFetching} onSubmit={handleSubmit} onReset={handleReset} />
        </VStack>
        <SingleBatchSettingTable
          gridRef={gridRef}
          items={items}
          totalCount={totalCount}
          isLoading={isFetching}
          currentPage={currentPage}
          countPerPage={countPerPage}
          onChangePage={handleChangePage}
          onCountPerPageChange={handleCountPerPageChange}
          onDelete={onDelete}
          onStatusChange={handleStatusChange}
          isStatusUpdating={isStatusUpdating}
          onPeriodEdit={handlePeriodEdit}
        />
      </PageLayout>
      {isRegisterModalOpen && (
        <SingleBatchSettingRegisterModal
          ref={registerModalRef}
          onClose={handleCloseRegisterModal}
          onSuccess={handleRegisterSuccess}
          onPartialFailure={handleRegisterPartialFailure}
          onExcelFailure={handleExcelFailure}
          onDirectFailure={handleDirectFailure}
          onFileReselected={handleFileReselected}
        />
      )}
      {resultVariant && (
        <SingleBatchSettingResultDialog
          variant={resultVariant}
          failedRows={failedRows}
          message={failureMessage}
          onClose={handleCloseResult}
          onReupload={handleReupload}
        />
      )}

      {deleteConfirmRows && deleteConfirmRows.length > 0 && (
        <SingleBatchSettingDeleteConfirmDialog
          rows={deleteConfirmRows}
          isPending={isDeleting}
          onClose={handleDeleteConfirmClose}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {periodEditRow && (
        <SingleBatchSettingPeriodEditModal
          row={periodEditRow}
          onClose={handlePeriodEditClose}
          onSuccess={handlePeriodEditSuccess}
          onDirectFailure={handlePeriodEditDirectFailure}
        />
      )}
    </FormProvider>
  )
}

export default SingleBatchSetting
